import { Box, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

const CARD_BASE = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 1.5,
  p: 3,
  width: 220,
  borderRadius: 2,
  cursor: 'pointer',
  transition: 'all 0.18s ease',
  outline: 'none',
  '&:focus-visible': {
    boxShadow: '0 0 0 3px #3b82f6',
  },
};

const AI_SCREENS = ['ai_input', 'jira_input'];

const CARDS = [
  {
    screen: 'ai_input',
    icon: <AutoAwesomeIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Generate with AI',
    description: 'Describe your test scenario and let AI fill in the details',
  },
  {
    screen: 'jira_input',
    icon: <AccountTreeIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Generate from Jira Story',
    description: 'Select a Jira story and generate multiple test cases',
  },
  {
    screen: 'form',
    icon: <EditNoteIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Write yourself',
    description: 'Fill in all test case details yourself',
  },
];

const CreateTestCaseChoiceScreen = ({ onChoice, hasAiAccess = false, aiAccessDeniedReason  }) => {
  return (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2,
      py: 4,
      px: 2,
    }}
  >
    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
      How would you like to create this test case?
    </Typography>

    <Box
      role="listbox"
      aria-label="Test case creation method"
      sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}
    >
      {CARDS.map(({ screen, icon, title, description, primary }) => {
        const showLock = AI_SCREENS.includes(screen) && !hasAiAccess;

        return (
          <Box
            key={screen}
            role="option"
            tabIndex={showLock ? -1 : 0}
            onClick={() => !showLock && onChoice(screen)}
            onKeyDown={(e) => {
              if (!showLock && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onChoice(screen);
              }
            }}
            sx={{
              ...CARD_BASE,
              border: '2px solid',
              borderColor: showLock ? 'divider' : 'primary.main',
              cursor: showLock ? 'not-allowed' : 'pointer',
              '& .card-icon': showLock ? { filter: 'grayscale(1)', opacity: 0.6 } : {},
              '& .card-title': showLock ? { color: 'text.disabled' } : {},
              '& .card-desc': showLock ? { color: 'text.disabled' } : {},
              '&:hover': showLock ? {} : {
                bgcolor: primary ? 'primary.50' : 'grey.50',
                boxShadow: 3,
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Box className="card-icon">{icon}</Box>
            <Typography className="card-title" variant="subtitle1" fontWeight={600} textAlign="center">
              {title}
            </Typography>
            <Typography className="card-desc" variant="caption" color="text.secondary" textAlign="center">
              {description}
            </Typography>
            {showLock && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <LockOutlinedIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                <Typography variant="caption" color="primary.main" fontWeight={500}>
                  {aiAccessDeniedReason || 'AI not available'}
                </Typography>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  </Box>
)};

export default CreateTestCaseChoiceScreen;